import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  Input,
  OnChanges,
  PLATFORM_ID,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { storyblokImageSizes, storyblokLowQualityMap } from '../../../consts';
import { preloadImage } from '../../../lib/preload-image';
import { PICFLOW_CONFIG } from '../../../picflow-config';
import { getPictureSourceSizes } from '../../../storyblok/custom-rich-text';
import { getStoryblokResizedUrl } from '../../../storyblok/get-resize-url';

@Component({
  selector: 'app-picture',
  templateUrl: './picture.component.html',
  styleUrls: ['./picture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [],
})
export class PictureComponent implements OnChanges {
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private config = inject(PICFLOW_CONFIG);

  @Input() url: string;
  @Input() mobileUrl: string;
  /**
   * Scale of the image between 0 and 1
   */
  @Input() scale: number = 1;
  @Input() width: number;
  @Input() height: number;
  @Input() maxWidth: number;
  @Input() mobileWidth: number;
  @Input() mobileHeight: number;
  @Input() loading = 'lazy';
  @Input() preload = false;
  @Input() alt: string;

  srcset: string;
  mobileSrcset: string;

  src: string;
  mobileSrc: string;

  sizes: string;

  fetchPriority: 'auto' | 'high' | 'low' = 'auto';
  isMobile = false;

  private preloaded = false;

  private calcImgdata(url: string) {
    const srcset = getPictureSourceSizes(url, this.maxWidth * this.scale).map(size => {
      const resizedUrl = getStoryblokResizedUrl(url, size, storyblokLowQualityMap[size], this.config.storyblokUrl);
      return {
        string: `${resizedUrl} ${size}w`,
        size,
        url: resizedUrl,
      };
    });

    const defaultSize = storyblokImageSizes[storyblokImageSizes.length - 2];
    return {
      srcset: srcset.map(s => s.string).join(', '),
      src: getStoryblokResizedUrl(url, defaultSize, storyblokLowQualityMap[defaultSize], this.config.storyblokUrl),
    };
  }

  ngOnChanges() {
    if (!this.url) return (this.srcset = undefined);
    if (this.loading === 'eager') this.fetchPriority = 'high';
    if (!this.loading) this.loading = 'lazy';

    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = ScreenSizes.screenSize() === 'mobile';
    }

    const { srcset, src } = this.calcImgdata(this.url);

    if (src && !src.endsWith('.svg')) {
      this.srcset = srcset;
    } else {
      this.srcset = '';
    }

    this.src = src;

    if (this.mobileSrc) {
      const { srcset, src } = this.calcImgdata(this.mobileSrc);
      this.mobileSrcset = srcset;
      this.mobileSrc = src;
    }

    const scale = this.scale || 1;
    const scaledMaxWidth = this.maxWidth * scale;
    // Guard against unset maxWidth (e.g. pageWidth "full") — never emit
    // "undefinedpx"/"NaNpx". Clause order mirrors actual rendering: fluid
    // (viewport-relative) below the container cap, fixed px above it.
    this.sizes =
      Number.isFinite(scaledMaxWidth) && scaledMaxWidth > 0
        ? `(max-width: 600px) 100vw, (max-width: ${this.maxWidth}px) ${scale * 100}vw, ${scaledMaxWidth}px`
        : `(max-width: 600px) 100vw, ${scale * 100}vw`;

    if (!this.preloaded && this.preload) {
      preloadImage(this.url, this.document, {
        fetchpriority: 'high',
        imagesrcset: this.srcset,
        imagesizes: this.sizes,
      });
      this.preloaded = true;
    }
  }

  getInSize(src: string, size: number) {
    return getStoryblokResizedUrl(src, size, 90, this.config.storyblokUrl);
  }
}
